import React, { useEffect, useMemo, useState } from "react";
import api from "../api";

export default function AdminPanel({
    selectedSite,
    currentFolderId = null,
    onAfterChange
}) {
    const [mode, setMode] = useState("create"); // 'create' | 'edit'
    const [type, setType] = useState("item"); // 'item' | 'folder'
    const [name, setName] = useState("");
    const [shortDescription, setShortDescription] = useState("");
    const [description, setDescription] = useState("");
    const [siteUrl, setSiteUrl] = useState("");
    const [parentId, setParentId] = useState(currentFolderId ?? "");
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");
    const [error, setError] = useState("");
    const [isPersonal, setIsPersonal] = useState(false);
    const [availableParents, setAvailableParents] = useState([]);
    const [loadingParents, setLoadingParents] = useState(false);

    const isFolder = type === "folder";
    const isEditMode = mode === "edit";
    const hasSelectedSite = Boolean(selectedSite);

    const parentOptions = useMemo(() => {
        if (!Array.isArray(availableParents)) return [];

        return availableParents.filter((item) => {
            if (item.type !== "folder") return false;
            if (isEditMode && selectedSite && item.id === selectedSite.id) return false;
            return true;
        });
    }, [availableParents, isEditMode, selectedSite]);

    const clearForm = (nextType = "item") => {
        setType(nextType);
        setName("");
        setShortDescription("");
        setDescription("");
        setSiteUrl("");
        setIsPersonal(false);
        setParentId(currentFolderId ?? "");
    };

    useEffect(() => {
        if (selectedSite && mode === "edit") {
            setType(selectedSite.type || "item");
            setName(selectedSite.name || "");
            setShortDescription(selectedSite.shortDescription || "");
            setDescription(selectedSite.description || "");
            setSiteUrl(selectedSite.siteUrl || "");
            setIsPersonal(!!selectedSite.isPersonal);
            setParentId(
                selectedSite.parentId === null || selectedSite.parentId === undefined
                    ? ""
                    : String(selectedSite.parentId)
            );
        } else if (mode === "create") {
            clearForm(type);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSite, mode]);

    useEffect(() => {
        if (mode === "create") {
            setParentId(currentFolderId ?? "");
        }
    }, [currentFolderId, mode]);

    useEffect(() => {
        const fetchParents = async () => {
            setLoadingParents(true);
            try {
                const token = localStorage.getItem("token");
                const res = await api.get("/sites", {
                    headers: token
                        ? { Authorization: `Bearer ${token}` }
                        : undefined
                });

                const items = Array.isArray(res.data) ? res.data : [];
                setAvailableParents(items);
            } catch (err) {
                console.error("Failed to load parent folders:", err);
            } finally {
                setLoadingParents(false);
            }
        };

        fetchParents();
    }, []);

    const normalizeParentId = () => {
        if (parentId === "" || parentId === null || parentId === undefined) {
            return null;
        }

        const parsed = Number(parentId);
        return Number.isNaN(parsed) ? null : parsed;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setMsg("");
        setLoading(true);

        if (!name.trim()) {
            setError("Назва є обов’язковою.");
            setLoading(false);
            return;
        }

        if (!isFolder && siteUrl && !/^https?:\/\//i.test(siteUrl)) {
            setError("URL має починатися з http:// або https://");
            setLoading(false);
            return;
        }

        const payload = {
            name: name.trim(),
            type,
            isPersonal,
            parentId: normalizeParentId(),
            shortDescription: shortDescription.trim() || null,
            description: description.trim() || null,
            siteUrl: isFolder ? null : siteUrl.trim() || null
        };

        try {
            if (isEditMode && selectedSite) {
                await api.patch(`/sites/${selectedSite.id}`, payload);
                setMsg(type === "folder" ? "Папку оновлено." : "Сайт оновлено.");
            } else {
                await api.post("/sites", payload);
                setMsg(type === "folder" ? "Папку створено." : "Сайт створено.");
                clearForm(type);
            }

            onAfterChange && onAfterChange();
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || "Помилка збереження.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedSite) return;

        const label =
            selectedSite.type === "folder"
                ? "Видалити цю папку разом із вмістом?"
                : "Видалити цей сайт?";

        if (!window.confirm(label)) return;

        setError("");
        setMsg("");
        setLoading(true);

        try {
            await api.delete(`/sites/${selectedSite.id}`);
            setMsg(selectedSite.type === "folder" ? "Папку видалено." : "Сайт видалено.");
            onAfterChange && onAfterChange();
            clearForm("item");
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || "Помилка видалення.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mt-8 border-t border-slate-200 pt-6 dark:border-slate-700">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-50">
                    Панель адміністратора
                </h3>

                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <input
                            id="isPersonal"
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400 dark:border-slate-600 dark:bg-slate-900"
                            checked={isPersonal}
                            onChange={(e) => setIsPersonal(e.target.checked)}
                        />
                        <label
                            htmlFor="isPersonal"
                            className="text-xs text-slate-600 dark:text-slate-300"
                        >
                            Персональний запис
                        </label>
                    </div>

                    <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-200">
                        <button
                            type="button"
                            onClick={() => {
                                setMode("create");
                                clearForm("item");
                            }}
                            className={
                                "px-3 py-1 rounded-full transition " +
                                (mode === "create"
                                    ? "bg-slate-900 text-slate-50 dark:bg-slate-100 dark:text-slate-900"
                                    : "text-slate-600 dark:text-slate-300")
                            }
                        >
                            Новий запис
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode("edit")}
                            className={
                                "px-3 py-1 rounded-full transition " +
                                (mode === "edit"
                                    ? "bg-slate-900 text-slate-50 dark:bg-slate-100 dark:text-slate-900"
                                    : "text-slate-600 dark:text-slate-300")
                            }
                        >
                            Редагувати вибране
                        </button>
                    </div>
                </div>
            </div>

            {mode === "edit" && !selectedSite && (
                <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
                    Вибери сайт або папку ліворуч для редагування.
                </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
                            Тип елемента
                        </label>
                        <select
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-slate-500 dark:focus:ring-slate-700"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            disabled={loading || (isEditMode && !selectedSite)}
                        >
                            <option value="item">Сайт</option>
                            <option value="folder">Папка</option>
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
                            Батьківська папка
                        </label>
                        <select
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-slate-500 dark:focus:ring-slate-700"
                            value={parentId}
                            onChange={(e) => setParentId(e.target.value)}
                            disabled={loading || loadingParents || (isEditMode && !selectedSite)}
                        >
                            <option value="">— Корінь каталогу —</option>
                            {parentOptions.map((folder) => (
                                <option key={folder.id} value={folder.id}>
                                    {folder.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
                            Назва
                        </label>
                        <input
                            type="text"
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-0 placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-slate-500 dark:focus:ring-slate-700"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </div>

                    {!isFolder && (
                        <div>
                            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
                                URL сайту
                            </label>
                            <input
                                type="url"
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-0 placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-slate-500 dark:focus:ring-slate-700"
                                value={siteUrl}
                                onChange={(e) => setSiteUrl(e.target.value)}
                                placeholder="https://example.com"
                            />
                        </div>
                    )}

                    <div className={!isFolder ? "" : "md:col-span-2"}>
                        <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
                            Короткий опис
                        </label>
                        <input
                            type="text"
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-0 placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-slate-500 dark:focus:ring-slate-700"
                            value={shortDescription}
                            onChange={(e) => setShortDescription(e.target.value)}
                            placeholder={
                                isFolder
                                    ? "Наприклад: Колекція frontend-проєктів"
                                    : "Короткий опис сайту"
                            }
                        />
                    </div>
                </div>

                <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
                        Детальний опис
                    </label>
                    <textarea
                        className="w-full min-h-[120px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-0 placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-slate-500 dark:focus:ring-slate-700"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder={
                            isFolder
                                ? "Опис вмісту папки"
                                : "Детальний опис сайту"
                        }
                    />
                </div>

                {error && (
                    <p className="text-xs text-red-500 dark:text-red-400">{error}</p>
                )}

                {msg && (
                    <p className="text-xs text-emerald-500 dark:text-emerald-400">
                        {msg}
                    </p>
                )}

                <div className="flex items-center gap-3">
                    <button
                        type="submit"
                        disabled={loading || (isEditMode && !selectedSite)}
                        className="inline-flex items-center justify-center rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-slate-50 shadow-sm hover:bg-black disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white transition-colors"
                    >
                        {loading
                            ? "Збереження..."
                            : isEditMode
                                ? "Зберегти зміни"
                                : isFolder
                                    ? "Створити папку"
                                    : "Створити сайт"}
                    </button>

                    {isEditMode && selectedSite && (
                        <button
                            type="button"
                            onClick={handleDelete}
                            disabled={loading}
                            className="inline-flex items-center justify-center rounded-full border border-red-500/70 px-4 py-2 text-sm font-medium text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-400/70 dark:text-red-300 dark:hover:bg-red-500/10 transition-colors"
                        >
                            Видалити
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
}